import React from "react";
import { Navigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { agentNavRoster, findTheoAgent } from "../navigation/theoAgents";
import { useAgents } from "../hooks/useAgents";
import { useUserMsp } from "../hooks/useUserMsp";
import { NEUTRAL } from "../styles/brandTokens";
import { AgentConfigure } from "./AgentConfigure";
import TheoAutomationsMonitor from "./TheoAutomationsMonitor";

const PANEL_COPY = {
  monitor: {
    label: "Monitor",
    body: "Runs, ticket outcomes, and where this agent is waiting will be listed here.",
  },
  configure: {
    label: "Configure",
    body: "Scope, rules, and how this agent takes work will be set here.",
  },
} as const;

/**
 * Monitor and Configure for an agent other than the default one.
 *
 * Monitor is the same summary and executions screen as `/theo-automations/monitor`, limited to this
 * agent's runs. Configure is the same setup screen every agent uses. The default agent keeps
 * `/theo-automations/configure` and is redirected there.
 *
 * `agentId` in the route is the agent's slug, which is immutable, so these URLs survive a rename.
 */
const AgentPlaceholder: React.FC = () => {
  const { agentId, panel } = useParams<{ agentId: string; panel: string }>();
  const { userMspInfo } = useUserMsp();
  const { agents, isLoading } = useAgents(userMspInfo?.msp_id);
  const roster = React.useMemo(() => agentNavRoster(agents), [agents]);
  const agent = findTheoAgent(agentId, roster);
  const panelCopy = panel === "monitor" || panel === "configure" ? PANEL_COPY[panel] : null;

  if (!panelCopy) {
    return <Navigate to="/theo-automations/monitor" replace />;
  }
  // Wait for the roster before deciding the agent does not exist, or a direct link or refresh
  // would bounce to the default agent's monitor every time.
  if (isLoading) {
    return <Box sx={{ width: "100%", height: "100%", background: NEUTRAL.page }} />;
  }
  if (!agent) {
    return <Navigate to="/theo-automations/monitor" replace />;
  }
  if (!agent.placeholder) {
    return <Navigate to={panel === "configure" ? agent.configureTo : agent.monitorTo} replace />;
  }
  if (panel === "monitor") {
    return <TheoAutomationsMonitor key={agent.id} agentId={agents.find((configured) => configured.slug === agent.id)?.id} />;
  }
  if (panel === "configure" && agentId) {
    return <AgentConfigure slug={agentId} />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        background: NEUTRAL.page,
        px: { xs: 2, sm: 4 },
        py: 4,
        fontFamily: "var(--theo-font-sans)",
      }}
    >
      <Box sx={{ maxWidth: 720, mx: "auto" }}>
        <Typography
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#6846BF",
          }}
        >
          AI Agent
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 0.75, flexWrap: "wrap" }}>
          <Typography
            component="h1"
            sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 28, fontWeight: 700, color: "#172033", lineHeight: 1.2 }}
          >
            {agent.title}
          </Typography>
          <Chip
            label="Screens coming soon"
            size="small"
            sx={{
              height: 24,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 600,
              fontSize: 12,
              color: "#6846BF",
              backgroundColor: "#F3F0FF",
            }}
          />
        </Box>
        <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 15, lineHeight: 1.6, color: "#5F6B7A", mt: 1.25 }}>
          {agent.summary}
        </Typography>

        <Box
          sx={{
            mt: 3,
            p: 3,
            borderRadius: "10px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E7EBF1",
          }}
        >
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, fontWeight: 700, color: "#6846BF" }}>
            {panelCopy.label}
          </Typography>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 16, fontWeight: 600, color: "#172033", mt: 0.75 }}>
            This agent is live. Its own {panelCopy.label.toLowerCase()} screen is not built yet.
          </Typography>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 14, lineHeight: 1.6, color: "#5F6B7A", mt: 1 }}>
            {panelCopy.body}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AgentPlaceholder;
